import { Location, ViewportScroller } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';

import { WindowService } from '../services/window.service';

@Injectable({
  providedIn: 'root',
})
export class NavigationHelperService {
  constructor(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    @Inject(WindowService) private windowService: any,
    protected viewportScroller: ViewportScroller,
    private readonly router: Router,
    private readonly location: Location
  ) {}

  public openExternalUrl(url: string, target = '_blank', rel = 'noopener') {
    this.windowService.nativeWindow.open(url, target, rel);
  }

  public isSupportedFile(url: string): boolean {
    return !!url.match(/.+\.(pdf)$/);
  }

  public isTelephoneLink(url: string): boolean {
    return url.startsWith('tel:');
  }

  public isExternalUrl(url: string): boolean {
    return /^http(?:s)?:\/\//.test(url.trim());
  }

  public scrollToAnchor(anchorId: string) {
    this.viewportScroller.scrollToAnchor(anchorId);
  }

  public stripUrl(url: string): string {
    const urlTree: UrlTree = this.router.parseUrl(url);

    urlTree.queryParams = {};
    urlTree.fragment = null;

    return urlTree.toString();
  }

  public getLocalAnchor(navigateToUrl: string): string | null {
    const currentUrl: string = this.location.path();
    const newUrl: string = this.stripUrl(navigateToUrl);

    if (navigateToUrl.startsWith('#') || currentUrl === newUrl) {
      return navigateToUrl.split('#')[1] ?? null;
    }

    return null;
  }

  public getLocalHref(navigateToUrl: string) {
    const currentUrl: string = this.location.path();
    let newUrl: string = navigateToUrl;

    if (navigateToUrl.startsWith('#')) {
      newUrl = `${currentUrl}#${navigateToUrl.split('#')[1]}`;
    }

    return newUrl;
  }

  public navigate(
    event: Event,
    url: string,
    shouldLaunchNewWindow: boolean
  ): void {
    const shouldAllowNavigation = this.shouldAllowNavigation(event, url);
    if (!shouldAllowNavigation) {
      return;
    }

    const anchorName = this.getLocalAnchor(url);
    if (anchorName !== null) {
      this.scrollToAnchor(anchorName);
    }

    const isExternalUrl = this.isExternalUrl(url);
    if (isExternalUrl) {
      const target = shouldLaunchNewWindow ? '_blank' : '_self';
      const winRel = shouldLaunchNewWindow ? 'noopener' : '';
      this.openExternalUrl(url, target, winRel);
    } else {
      // navigateByUrl must take an absolute path
      if (!url.startsWith('/')) {
        url = `/${url}`;
      }
      this.router.navigateByUrl(url);
    }
  }

  private shouldAllowNavigation(event: Event, url: string): boolean {
    // If the link is a telephone link, do not prevent default browser behavior of opening the dialer app
    if (this.isTelephoneLink(url)) {
      return false;
    } else {
      // For all other links, prevent the default link behavior
      event.preventDefault();
    }

    // If the link is empty, do not navigate
    if (url === '') {
      return false;
    }

    // If the link is a print link, print the page and do not navigate
    if (url === 'javascript:window.print()') {
      this.windowService.print();

      return false;
    }

    return true;
  }
}
